package dev.lingvo.shared

import kotlinx.serialization.Serializable

// ---------------------------------------------------------------------------
// Glossary
// ---------------------------------------------------------------------------

@Serializable
data class GlossaryTranslationDto(
    val locale: String,
    val value: String,
)

@Serializable
data class GlossaryTermDto(
    val id: String,
    val term: String,
    val description: String? = null,
    val caseSensitive: Boolean = false,
    val doNotTranslate: Boolean = false,
    val translations: List<GlossaryTranslationDto> = emptyList(),
    val createdAt: String,
)

@Serializable
data class UpsertGlossaryTermRequest(
    val term: String,
    val description: String? = null,
    val caseSensitive: Boolean = false,
    val doNotTranslate: Boolean = false,
    val translations: List<GlossaryTranslationDto> = emptyList(),
)

// ---------------------------------------------------------------------------
// Translation providers
// ---------------------------------------------------------------------------

@Serializable
enum class ProjectProviderMode {
    INHERIT,
    OVERRIDE,
    OFF,
}

@Serializable
enum class ProviderCredentialSource {
    PROJECT,
    WORKSPACE,
    PLATFORM,
    NONE,
}

@Serializable
enum class ProviderReadiness {
    READY,
    NEEDS_SETUP,
    OFF,
    UNAVAILABLE,
}

/** Provider configuration as exposed by the API. Never carries the API key itself. */
@Serializable
data class ProviderSettingsDto(
    val provider: MtProvider,
    /**
     * Retained for old clients. New clients must use [readiness], which describes the resolved
     * credential rather than the presence of a project-row key.
     */
    val enabled: Boolean = false,
    val mode: ProjectProviderMode = ProjectProviderMode.INHERIT,
    val credentialSource: ProviderCredentialSource = ProviderCredentialSource.NONE,
    val readiness: ProviderReadiness = ProviderReadiness.NEEDS_SETUP,
    /** Safe explanation for [ProviderReadiness.UNAVAILABLE] or [ProviderReadiness.NEEDS_SETUP]. */
    val unavailableReason: String? = null,
    val hasKey: Boolean = false,
    /** True when this project owns a stored key, even while another credential is effective. */
    val hasStoredProjectKey: Boolean = false,
    /** Masked hint for a project-owned credential only; never a workspace or platform key. */
    val keyHint: String? = null,
    val baseUrl: String? = null,
    val model: String? = null,
    val requiresKey: Boolean = true,
    val supportsContext: Boolean = false,
    /**
     * True when a key is stored but cannot be decrypted — typically `LINGVO_SECRET_KEY` was rotated
     * or a database dump was restored without it. Distinct from [hasKey] being false: there is
     * something stored, it just cannot be used, and the owner has to enter it again. This remains
     * true while the declared mode is INHERIT or OFF so dormant ciphertext is never treated as a
     * reusable key when the project returns to OVERRIDE.
     */
    val keyUnreadable: Boolean = false,
    val updatedAt: String? = null,
)

@Serializable
data class UpdateProviderSettingsRequest(
    /** Explicit credential-resolution choice for new clients. */
    val mode: ProjectProviderMode? = null,
    /**
     * Legacy compatibility only. `false` maps to OFF; a submitted non-blank key maps to OVERRIDE;
     * an empty key maps to INHERIT. New clients must send [mode].
     */
    val enabled: Boolean? = null,
    /** Write-only. `null` leaves the stored key untouched; empty string clears it. */
    val apiKey: String? = null,
    /** Omission keeps the stored value; an explicit JSON null clears it while overriding. */
    val baseUrl: String? = null,
    /** Omission keeps the stored value; an explicit JSON null clears it while overriding. */
    val model: String? = null,
)

@Serializable
data class ProviderTestResultDto(
    val ok: Boolean,
    val message: String,
    val sample: String? = null,
    /** True only when the test used Lingvo's metered platform credential. */
    val consumedLingvoCredit: Boolean = false,
)

// ---------------------------------------------------------------------------
// Machine translation
// ---------------------------------------------------------------------------

@Serializable
data class MtTranslateRequest(
    val provider: MtProvider,
    val locale: String,
    val keyIds: List<String>,
    val overwrite: Boolean = false,
    val save: Boolean = false,
    val tone: String? = null,
)

@Serializable
data class MtResultDto(
    val keyId: String,
    val keyName: String,
    val value: String? = null,
    val pluralForms: Map<PluralCategory, String>? = null,
    val accepted: Boolean,
    val rejectionReason: String? = null,
)

@Serializable
data class MtTranslateResponse(
    val provider: MtProvider,
    val locale: String,
    val results: List<MtResultDto> = emptyList(),
    val accepted: Int = 0,
    val rejected: Int = 0,
    val saved: Int = 0,
)

/**
 * A queued machine-translation run.
 *
 * Every field after [locales] narrows what the run covers, and all of them default to "everything",
 * so a client written against the first version of this request still asks for the same work. The
 * narrowing exists because the counts a page draws are always about a slice — the strings behind one
 * tag, one file, or the rows a filter matched — and an automation that translated the whole project
 * when the button said "pre-translate 412 untranslated" would spend money nobody agreed to.
 */
@Serializable
data class MtPretranslateRequest(
    val provider: MtProvider,
    val locales: List<String>,
    val onlyEmpty: Boolean = true,
    val tone: String? = null,
    /** Only keys carrying this tag. Null covers every key. */
    val tag: String? = null,
    /** Only keys in this file. Null covers every file. */
    val fileId: String? = null,
    /**
     * Exactly these keys, at most [MAX_MT_PRETRANSLATE_KEYS] of them.
     *
     * Empty means "no key filter", not "no keys": an empty selection would otherwise be a request to
     * translate the whole project, which is the opposite of what an empty selection means anywhere
     * a person can make one.
     */
    val keyIds: List<String> = emptyList(),
    /**
     * Fill from the workspace's translation memory and stop there.
     *
     * A free automation: the memory phase runs, the provider is never called, and nothing is
     * reserved. Worth being a first-class request rather than a client convention, because
     * "translate what we already know the answer to" is a different decision from "buy translations"
     * and the person making it should not have to reason about credits at all.
     */
    val memoryOnly: Boolean = false,
)

/** What one locale of a planned run would cost, before anybody presses the button. */
@Serializable
data class MtLocaleEstimateDto(
    val locale: String,
    /** Strings the selection matched in this locale. */
    val strings: Int = 0,
    /** How many of those the translation memory can fill for nothing. */
    val memoryPrefillable: Int = 0,
    /** What is left for the provider, and therefore what is priced. */
    val providerBound: Int = 0,
    /** Source characters across [providerBound], counted the way the charge is counted. */
    val sourceChars: Int = 0,
    /** Credits this locale would reserve. Zero on a project using its own provider key. */
    val credits: Int = 0,
)

/**
 * What a pre-translate would cost, computed by the server that would charge for it.
 *
 * **The estimate and the job share one function.** Both are produced by `planMtJob`, so the number
 * in the confirmation dialog is not a client-side approximation of the server's arithmetic — it is
 * the server's arithmetic, run without writing anything. An estimate that can disagree with the
 * charge is worse than no estimate, because it is believed.
 */
@Serializable
data class MtEstimateDto(
    val perLocale: List<MtLocaleEstimateDto> = emptyList(),
    val totalCredits: Int = 0,
    /** What the workspace has left this month, so a surface can say "and this many after". */
    val creditsRemaining: Int = 0,
    /** True when the project translates on its own provider key, so the run costs no credits. */
    val byok: Boolean = false,
    /**
     * Why this run could not start, in the workspace's words.
     *
     * Present when the provider is disabled, unusable, or missing a key — the counts above are still
     * true, and telling somebody what it would cost while hiding that it cannot run is how a
     * disabled provider becomes a support ticket.
     */
    val blockedReason: String? = null,
)

/**
 * One string a run refused to keep, and why.
 *
 * **A rejection is a row, not a sentence in a paragraph.** These used to be appended to the job's
 * free-text error as `keyName: reason` lines, which meant a page could only read them back by
 * loading every key name in the project and testing each line against all of them by prefix — and
 * a project with both `save` and `save.confirm` cannot be split that way at all.
 */
@Serializable
data class MtRejectionDto(
    val keyName: String,
    val reason: String,
)

/**
 * How many rejections one run records in full.
 *
 * A run that rejects a thousand strings has one defect a thousand times, and [MtJobDto.rejected]
 * already carries the count. Keeping the first few and saying how many more there were is the
 * difference between a receipt and a log file — and it keeps the jobs list from carrying a thousand
 * rows per job it only draws a badge for.
 */
const val MAX_MT_JOB_REJECTIONS = 50

@Serializable
data class MtJobDto(
    val id: String,
    val provider: MtProvider,
    val locales: List<String> = emptyList(),
    val state: MtJobState = MtJobState.RUNNING,
    val total: Int = 0,
    val done: Int = 0,
    val failed: Int = 0,
    val rejected: Int = 0,
    val error: String? = null,
    val createdAt: String,
    val finishedAt: String? = null,
    /** Credits held when it started. Zero for a run on the project's own provider key. */
    val reservedCredits: Int = 0,
    /**
     * When the reservation was given back.
     *
     * **This, not [state], is the source of truth for "was this refunded".** A job killed with the
     * process shows RUNNING for ever while the reaper has already refunded it, so a page that read
     * the state would show a spinner that never ends beside credits the workspace already has back.
     */
    val refundedAt: String? = null,
    /**
     * What actually came back, which is not always what was held.
     *
     * **[reservedCredits] is the wrong number to show a customer as "given back".** A job reaped
     * after delivering half its strings is refunded for half — the reaper computes
     * `reserved × (total − done) / total`, because returning the whole reservation while the
     * translations it already produced stay in the project hands the workspace the work *and* the
     * money. The receipt used to print the reservation, so it told people they had more back than
     * they did, which is the direction of error that gets found by someone reconciling a balance
     * weeks later.
     *
     * Null means no refund has been recorded — either none happened, or the row predates the column.
     * Deliberately not back-filled from [reservedCredits]: that value is a guess for exactly the
     * partial refunds this field exists to record.
     */
    val refundedCredits: Int? = null,
    /**
     * When somebody stopped this run on purpose.
     *
     * **A cancelled run is FAILED on the wire, deliberately.** Adding a state would break every
     * client that switches on [MtJobState] exhaustively, including a CLI somebody has already
     * shipped — and "stopped" is a fact about how the run ended rather than a different kind of
     * ending. Null on a run that failed by itself, which is the difference between "the provider
     * broke" and "we changed our mind", and the receipt says different things about the two.
     */
    val cancelledAt: String? = null,
    /**
     * Whether the run filled gaps only, or rewrote translations that already existed.
     *
     * Half of the intent a retry reproduces, and the half a receipt has to state: "translated 412
     * empty strings" and "replaced 412 translations" are different events, and until this was
     * recorded the jobs list could not tell them apart.
     */
    val onlyEmpty: Boolean = true,
    /** The tone the run was started with, if any. The other half of the intent. */
    val tone: String? = null,
    /**
     * The failed run this one repeats.
     *
     * Present only on a run started by "Try again". It is what lets a receipt say which attempt this
     * is rather than showing two unrelated runs over the same locale minutes apart.
     */
    val retryOf: String? = null,
    /**
     * The strings this run refused to keep, up to [MAX_MT_JOB_REJECTIONS] of them.
     *
     * Shorter than [rejected] when a run rejected more than the cap, which is the case a surface has
     * to say something about rather than quietly showing fewer rows than the count beside it.
     */
    val rejections: List<MtRejectionDto> = emptyList(),
) {
    val percent: Int get() = if (total <= 0) 0 else ((done.toLong() * 100L) / total).toInt()

    /** Stopped on purpose rather than by a failure. */
    val cancelled: Boolean get() = cancelledAt != null

    /**
     * Stopped without finishing and without failing cleanly.
     *
     * A refunded RUNNING job is the signature of a machine that suspended or a deploy that rolled
     * mid-run — ordinary on a host that scales to zero. Worth its own state because "still working"
     * and "stopped, and you have your credits back" are opposite messages.
     */
    val abandoned: Boolean get() = state == MtJobState.RUNNING && refundedAt != null
}

/**
 * A provider key held by the workspace rather than by one project.
 *
 * Separate from [ProviderSettingsDto] because it answers a different question — "what does this
 * workspace share with all its projects" — and carries [testedOkAt], which a project row does not
 * have.
 */
@Serializable
data class WorkspaceProviderDto(
    val provider: MtProvider,
    val enabled: Boolean = false,
    val hasKey: Boolean = false,
    /** Masked hint such as `sk-…4f2a`. Never the key. */
    val keyHint: String? = null,
    /**
     * A key is stored but cannot be decrypted: `LINGVO_SECRET_KEY` was rotated, or a dump was
     * restored without it. Distinct from having no key — there is something there, it cannot be
     * used, and re-entering it is the only fix.
     */
    val keyUnreadable: Boolean = false,
    val baseUrl: String? = null,
    val model: String? = null,
    val requiresKey: Boolean = true,
    /**
     * When a test last passed, or null for "stored, never tested".
     *
     * The honest label for a key nobody has proved works. Otherwise someone pastes a key with a
     * trailing newline, sees a green Saved, and discovers it three days later mid-pretranslate.
     */
    val testedOkAt: String? = null,
    val updatedAt: String? = null,
    /**
     * The same four-state answer [ProviderSettingsDto.readiness] gives a project.
     *
     * One vocabulary across both surfaces, so "does this workspace have a working provider" is the
     * same question in the shell, on the settings page and on a project — and so the shell can
     * answer it from the list it already loads rather than from a second request and a second
     * opinion. [enabled] and [hasKey] are retained for old clients and are the wrong pair to reason
     * with: a key that will not decrypt is stored, enabled, and unusable.
     */
    val readiness: ProviderReadiness = ProviderReadiness.NEEDS_SETUP,
    /** Safe explanation for anything other than [ProviderReadiness.READY]. Never a provider body. */
    val unavailableReason: String? = null,
)

/** Saving a workspace provider key. Save and test are one action; see the route. */
@Serializable
data class UpdateWorkspaceProviderRequest(
    val enabled: Boolean? = null,
    /** Write-only. Null leaves the stored key alone; empty clears it. */
    val apiKey: String? = null,
    val baseUrl: String? = null,
    val model: String? = null,
)

/** What saving did, and whether the key actually works. */
@Serializable
data class WorkspaceProviderSaveResultDto(
    val provider: WorkspaceProviderDto,
    /** Null when there was no key to test. */
    val testPassed: Boolean? = null,
    /**
     * The test's message, safe to show.
     *
     * Never a raw provider response body: a rejected request is frequently echoed back with the
     * submitted credential in it.
     */
    val testMessage: String? = null,
)

/**
 * One machine-translation run, as the jobs list shows it.
 *
 * [actor] is **absent** for translators and viewers rather than blanked by the page: who spent the
 * workspace's money is a management fact, and a name that reaches the browser has already been
 * disclosed whatever the interface then does with it. They see the run and its outcome, which is
 * what affects their work.
 */
@Serializable
data class MtJobSummaryDto(
    val job: MtJobDto,
    val actor: String? = null,
)

@Serializable
data class MtJobsDto(
    val jobs: List<MtJobSummaryDto> = emptyList(),
    /**
     * Feed back as `?after=` for the next page, or null when this is the last one.
     *
     * Null on a short page rather than always present: there is demonstrably nothing after it, and
     * offering a cursor into an empty page invites one more round trip per list.
     */
    val nextCursor: String? = null,
)
